import * as React from 'react';
import { observer } from 'mobx-react-lite';
import type { OwaDate } from 'owa-datetime';
import { InlineDatePicker } from 'owa-datepicker';
import type { ProjectId, AssetId } from 'owa-timestream-types';
import DueDateCompletionCheckbox from './DueDateCompletionCheckbox';
import DueDateRemoveButton from './DueDateRemoveButton';
import useSetDueDate from '../hooks/useSetDueDate';
import useSetIsComplete from '../hooks/useSetIsComplete';

export default observer(function DueDateMenuPicker({
    projectId,
    assetId,
    selectedDate,
    isCompleted,
    deleteTaskOnDateRemoval,
    dismissMenu,
    onSetDueDate,
    onRemoveDueDate,
    onSetComplete,
}: {
    projectId: ProjectId;
    assetId: AssetId;
    selectedDate: OwaDate | undefined;
    isCompleted: boolean;
    deleteTaskOnDateRemoval: boolean;
    dismissMenu?: (ev?: any, dismissAll?: boolean) => void;
    onSetDueDate?: (date: OwaDate | null) => void;
    onRemoveDueDate?: () => void;
    onSetComplete?: (isComplete: boolean) => void;
}) {
    const setDueDate = useSetDueDate(projectId, assetId, deleteTaskOnDateRemoval);
    const setIsComplete = useSetIsComplete(projectId, assetId);

    const onSelectDate = React.useCallback(
        (date: OwaDate) => {
            !!onSetDueDate ? onSetDueDate(date) : setDueDate(date);
            dismissMenu?.(undefined, true);
        },
        [onSetDueDate, setDueDate]
    );

    const onRemoveDate = React.useCallback(() => {
        !!onRemoveDueDate ? onRemoveDueDate() : setDueDate(null);
        dismissMenu?.(undefined, true);
    }, [onRemoveDueDate, setDueDate]);

    const onCheck = React.useCallback(
        (newValue: boolean) => {
            !!onSetComplete ? onSetComplete(newValue) : setIsComplete(newValue);
            dismissMenu?.(undefined, true);
        },
        [onSetComplete, setIsComplete]
    );

    return (
        <>
            <InlineDatePicker value={selectedDate} onSelectDate={onSelectDate} />
            <DueDateCompletionCheckbox
                isCompleted={isCompleted}
                disabled={!selectedDate}
                onIsCompletedChanged={onCheck}
            />
            {!!selectedDate && <DueDateRemoveButton onRemoveDueDate={onRemoveDate} />}
        </>
    );
});
